/**
 * Centralized resource clients for every backend CRUD endpoint exposed.
 * Add an entry here when adding a new module so module code never has to
 * construct a `new CrudService(...)` itself.
 */
import { CrudService, JunctionService } from './crud.service';
import { api } from './api';
import type { OneEnvelope } from '../types/api';
import type {
  AiAssistantRole,
  AiAssistantRoleSkill,
  AiAssistantRoleTool,
  AiChatMessage,
  AiChatSession,
  Document,
  DocumentAccess,
  DocumentCategory,
  DocumentSection,
  DocumentType,
  DocumentTypeEntityType,
  DocumentTypeSection,
  EntityType,
  EntityTypeSkill,
  Item,
  ItemDetail,
  ItemTag,
  Job,
  LlmRequest,
  Project,
  ProjectNote,
  ProjectValidationSummary,
  Tag,
  Task,
  Tenant,
  TenantRole,
  User,
  UserTenant,
} from '../types/data-models';

// ---------------------------------------------------------------------------
// LLM Agent — non-CRUD job endpoints. The agent service lives under
// `/api/v1/llm-agent/*`; the helpers below are thin wrappers so callers
// don't need to touch axios directly.
// ---------------------------------------------------------------------------

export type LlmJobState =
  | 'pending'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface LlmJobStep {
  step?: string;
  name?: string;
  status?: string;
  startedAt?: string;
  endedAt?: string;
  finishedAt?: string;
  detail?: string;
}

export interface LlmJobStatus<TResult = unknown> {
  jobId: string;
  state: LlmJobState;
  steps: LlmJobStep[];
  progress?: number;
  result?: TResult;
  error?: string;
  startedAt?: string;
  finishedAt?: string;
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Document type sections preview — renders the exact {DOCUMENT_SECTIONS}
// block generate-document.job.ts builds for a document type, so previews
// shown in the backoffice always match the real generation prompt.
// ---------------------------------------------------------------------------

export const documentTypeSectionsPreview = {
  async get(documentType: string): Promise<string> {
    const res = await api.get<OneEnvelope<{ documentSections: string }>>(
      `/document-types/${documentType}/sections-preview`,
    );
    return res.data.data.documentSections;
  },
};

export const llmAgent = {
  async getJob<TResult = unknown>(jobId: string): Promise<LlmJobStatus<TResult>> {
    const res = await api.get<OneEnvelope<LlmJobStatus<TResult>>>(
      `/llm-agent/jobs/${jobId}`,
    );
    return res.data.data;
  },
  async cancelJob(jobId: string): Promise<LlmJobStatus> {
    const res = await api.post<OneEnvelope<LlmJobStatus>>(
      `/llm-agent/jobs/${jobId}/cancel`,
    );
    return res.data.data;
  },
};

export const resources = {
  // identity / tenant
  tenants: new CrudService<Tenant>('/tenants'),
  users: new CrudService<User>('/users'),

  // global catalog
  tags: new CrudService<Tag>('/tags'),
  documentCategories: new CrudService<DocumentCategory>('/document-categories'),
  documentTypes: new CrudService<DocumentType>('/document-types'),
  documentTypeSections: new CrudService<DocumentTypeSection>('/document-type-sections'),
  entityTypes: new CrudService<EntityType>('/entity-types'),
  entityTypeSkills: new CrudService<EntityTypeSkill>('/entity-type-skills'),
  documentTypeEntityTypes: new CrudService<DocumentTypeEntityType>('/document-type-entity-types'),
  aiAssistantRoles: new CrudService<AiAssistantRole>('/ai-assistant-roles'),
  aiAssistantRoleSkills: new CrudService<AiAssistantRoleSkill>('/ai-assistant-role-skills'),
  aiAssistantRoleTools: new CrudService<AiAssistantRoleTool>('/ai-assistant-role-tools'),

  // tenant-scoped Base
  projects: new CrudService<Project>('/projects'),
  items: new CrudService<Item>('/items'),
  itemDetails: new CrudService<ItemDetail>('/item-details'),
  tasks: new CrudService<Task>('/tasks'),
  llmRequests: new CrudService<LlmRequest>('/llm-requests'),
  // Backoffice-only audit view — cross-user, read-only (see backend
  // ai-assistant/monitoring). Not the console's own per-user session API.
  aiChatMonitoringSessions: new CrudService<AiChatSession>('/ai-assistant/monitoring/sessions'),
  jobs: Object.assign(new CrudService<Job>('/jobs'), {
    /** POST /jobs/:id/cancel — request cancellation of a PENDING or PROCESSING job. */
    cancel: async (id: number): Promise<Job> => {
      const res = await api.post<OneEnvelope<Job>>(`/jobs/${id}/cancel`);
      return res.data.data;
    },
  }),

  // docs domain
  documents: new CrudService<Document>('/documents'),
  documentSections: new CrudService<DocumentSection>('/document-sections'),
  documentAccess: new CrudService<DocumentAccess>('/document-access'),

  // Scoped under a parent
  projectNotesFor: (projectId: number) =>
    new CrudService<ProjectNote>(`/projects/${projectId}/notes`),

  /** The project's idea-validation summary; null until it was generated. */
  validationSummaryFor: async (projectId: number): Promise<ProjectValidationSummary | null> => {
    const res = await api.get<OneEnvelope<ProjectValidationSummary | null>>(`/projects/${projectId}/validation-summary`);
    return res.data.data;
  },
  aiChatMonitoringMessagesFor: (sessionId: number) =>
    new CrudService<AiChatMessage>(`/ai-assistant/monitoring/sessions/${sessionId}/messages`),

  // Junction (M:N) — attach/detach by the related id
  itemTagsFor: (itemId: number) => new JunctionService<ItemTag>(`/items/${itemId}/tags`),
  tenantUsersFor: (tenantId: number) =>
    new JunctionService<UserTenant, { role: TenantRole }>(`/tenants/${tenantId}/users`),

  /**
   * Kick off the LLM-backed "Generate items" job for one project.
   * The result is consumed by the `GenerateItemsDialog` flow:
   * poll `/llm-agent/jobs/:jobId` until terminal, then let the user
   * pick which suggestions to persist via `resources.items.create`.
   */
  generateItems: async (
    projectId: number,
    body: { count?: number; hint?: string } = {},
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/items/generate-items/${projectId}`, body);
    return res.data.data;
  },
} as const;
